import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useEditorState } from '@tiptap/react';
import { Box, ButtonBase, InputBase, Paper, SvgIcon, Tooltip } from '@mui/material';
import {
  Check,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  SwapHoriz,
} from '@mui/icons-material';
import imageService from '../../services/imageService';
import { normalizeImageAlign, normalizeImageWrap } from './editorExtensions';
import { EDITABLE_EVENT } from './useEditorEditable';

/**
 * Image toolbar: a bubble menu over the selected image (NodeSelection) in an editable editor.
 * Align left / centre / right (`data-align`), wrap text none / left / right (`data-wrap`), size
 * presets of the content width, replace (re-upload keeping alt text and placement) and alt text.
 *
 * Sizes are stored the way the resize handles store them: `width` / `height` in px, computed
 * against the width of the column the image sits in at the time (so "Medium" is half the
 * column, inside a table cell or a generated section too).
 *
 * Keyboard: Alt+F10 in the editor moves focus into the toolbar while an image is selected,
 * arrow keys move between its buttons and Escape returns to the editor with the image still
 * selected.
 */

/** Size presets (share of the content width). */
export const IMAGE_SIZE_PRESETS = [
  { id: 'small', label: 'Small', short: 'S', ratio: 0.25 },
  { id: 'medium', label: 'Medium', short: 'M', ratio: 0.5 },
  { id: 'large', label: 'Large', short: 'L', ratio: 0.75 },
  { id: 'full', label: 'Full width', short: 'Full', ratio: 1 },
];

/**
 * Widest a wrapped image is drawn, as a share of the column (editorContentStyles caps a
 * data-wrap container at max-width 60%; the Word, PDF and HTML exports cap it the same way).
 * Presets wider than this (Large, Full width) stop the wrap, so the picture really is that wide.
 */
export const WRAPPED_IMAGE_MAX_SHARE = 0.6;

/** Smallest width a preset produces (the resize handles' minimum). */
export const MIN_IMAGE_WIDTH = 50;
/** Content width used when the column cannot be measured (A4 page with 1 in margins, in px). */
export const FALLBACK_CONTENT_WIDTH = 650;

/**
 * Width of the column an element sits in: its parent's content box.
 * @param {Element|null|undefined} element - The image's node view element.
 * @returns {number} Width in px (0 when unknown).
 */
export const contentWidthOf = (element) => {
  const parent = element?.parentElement;
  if (!parent) return 0;
  let padding = 0;
  try {
    const style = window.getComputedStyle(parent);
    padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
  } catch {
    padding = 0;
  }
  return Math.max(0, (parent.clientWidth || 0) - padding);
};

/**
 * Width and height for a size preset, keeping the picture's aspect ratio.
 * @param {{ratio: number, contentWidth?: number, naturalWidth?: number, naturalHeight?: number, width?: number|null, height?: number|null}} input - Preset ratio, column width, the image's natural size and its current attributes.
 * @returns {{width: number, height: number|null}} New size (height null when the ratio is unknown).
 */
export const presetImageSize = ({ ratio, contentWidth, naturalWidth, naturalHeight, width, height }) => {
  const column = contentWidth > 0 ? contentWidth : FALLBACK_CONTENT_WIDTH;
  const nextWidth = Math.max(MIN_IMAGE_WIDTH, Math.round(column * ratio));
  let aspect = null;
  if (naturalWidth > 0 && naturalHeight > 0) aspect = naturalHeight / naturalWidth;
  else if (Number(width) > 0 && Number(height) > 0) aspect = Number(height) / Number(width);
  return { width: nextWidth, height: aspect ? Math.max(1, Math.round(nextWidth * aspect)) : null };
};

/**
 * The preset an image's width matches (within 2 px), if any. A wrapped image is measured at the
 * width it is drawn at (capped at {@link WRAPPED_IMAGE_MAX_SHARE} of the column).
 * @param {number|null} width - Current width attribute.
 * @param {number} contentWidth - Column width.
 * @param {string} [wrap] - Current wrap ('none' | 'left' | 'right').
 * @returns {string|null} Preset id.
 */
export const activeSizePreset = (width, contentWidth, wrap = 'none') => {
  let w = Number(width);
  if (!(w > 0)) return null;
  const column = contentWidth > 0 ? contentWidth : FALLBACK_CONTENT_WIDTH;
  if (normalizeImageWrap(wrap) !== 'none') w = Math.min(w, Math.round(column * WRAPPED_IMAGE_MAX_SHARE));
  const hit = IMAGE_SIZE_PRESETS.find((preset) => Math.abs(Math.max(MIN_IMAGE_WIDTH, Math.round(column * preset.ratio)) - w) <= 2);
  return hit ? hit.id : null;
};

/**
 * Whether a size preset is too wide to keep text beside the picture.
 * @param {{ratio: number}} preset - Size preset.
 * @returns {boolean} True for Large and Full width.
 */
export const presetStopsWrap = (preset) => Number(preset?.ratio) > WRAPPED_IMAGE_MAX_SHARE;

/**
 * Placement after choosing a size preset: a preset wider than the wrap cap stops the wrap (Full
 * width also centres the picture, since it spans the column); others keep the placement.
 * @param {{id: string, ratio: number}} preset - Size preset.
 * @param {string} align - Current alignment.
 * @param {string} wrap - Current wrap.
 * @returns {{align: string, wrap: string}|null} Placement change, or null when it stays.
 */
export const placementForSize = (preset, align, wrap) => {
  if (preset?.id === 'full') return { wrap: 'none', align: 'center' };
  if (normalizeImageWrap(wrap) !== 'none' && presetStopsWrap(preset)) return placementForWrap('none', align);
  return null;
};

/**
 * Placement after choosing an alignment: a centred image cannot have text beside it, and a
 * wrapped image follows the side it moves to.
 * @param {string} align - Chosen alignment.
 * @param {string} wrap - Current wrap.
 * @returns {{align: string, wrap: string}} Attributes.
 */
export const placementForAlign = (align, wrap) => {
  const nextAlign = normalizeImageAlign(align);
  const current = normalizeImageWrap(wrap);
  return { align: nextAlign, wrap: current === 'none' || nextAlign === 'center' ? 'none' : nextAlign };
};

/**
 * Placement after choosing a wrap: a wrapped image is aligned to its side (so viewers that do
 * not float images still show it there); unwrapping keeps the alignment.
 * @param {string} wrap - Chosen wrap.
 * @param {string} align - Current alignment.
 * @returns {{align: string, wrap: string}} Attributes.
 */
export const placementForWrap = (wrap, align) => {
  const nextWrap = normalizeImageWrap(wrap);
  return { wrap: nextWrap, align: nextWrap === 'none' ? normalizeImageAlign(align) : nextWrap };
};

/**
 * Attributes for a replaced picture: new source and file, same alt text and placement; the
 * width stays (capped like a fresh upload when there was none) and the height follows the new
 * picture's aspect ratio.
 * @param {object} attrs - Current image attributes.
 * @param {{dataUrl: string, id?: string, name?: string, width?: number, height?: number}} upload - Upload result.
 * @returns {object} Attributes to apply.
 */
export const replacedImageAttrs = (attrs, upload) => {
  const current = Number(attrs?.width) > 0 ? Number(attrs.width) : null;
  const width = current || (upload.width ? Math.min(upload.width, 600) : null);
  const height = width && upload.width > 0 && upload.height > 0 ? Math.max(1, Math.round((width * upload.height) / upload.width)) : null;
  return {
    src: upload.dataUrl,
    title: upload.name || attrs?.title || null,
    'data-image-id': upload.id || null,
    width,
    height,
  };
};

/** Wrap icons: a picture block with text lines beside it (or above and below for none). */
const WrapNoneIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4 4h16v2H4zM8 8h8v8H8zM4 18h16v2H4z" />
  </SvgIcon>
);
const WrapLeftIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4 5h8v8H4zM14 5h6v2h-6zM14 9h6v2h-6zM14 13h6v2h-6zM4 17h16v2H4z" />
  </SvgIcon>
);
const WrapRightIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 5h8v8h-8zM4 5h6v2H4zM4 9h6v2H4zM4 13h6v2H4zM4 17h16v2H4z" />
  </SvgIcon>
);

const ToolButton = ({ label, active, onClick, children, wide = false, toggle = true }) => (
  <Tooltip title={label} disableInteractive>
    <ButtonBase
      aria-label={label}
      aria-pressed={toggle ? Boolean(active) : undefined}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      sx={{
        minWidth: 30,
        height: 30,
        px: wide ? 0.75 : 0,
        borderRadius: '7px',
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: 'var(--q-font-sans, inherit)',
        color: active ? 'var(--q-tooltip-text)' : 'color-mix(in srgb, var(--q-tooltip-text) 86%, transparent)',
        bgcolor: active ? 'color-mix(in srgb, var(--q-tooltip-text) 18%, transparent)' : 'transparent',
        '&:hover': { bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 14%, transparent)' },
        '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid var(--q-tooltip-text)', outlineOffset: '-2px' },
        '& svg': { fontSize: 18 },
      }}
    >
      {children}
    </ButtonBase>
  </Tooltip>
);

const Divider = () => (
  <Box aria-hidden sx={{ width: '1px', height: 18, bgcolor: 'color-mix(in srgb, var(--q-tooltip-text) 24%, transparent)', mx: 0.5, flexShrink: 0 }} />
);

const paperSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '2px',
  p: 0.5,
  maxWidth: 'calc(100vw - 32px)',
  borderRadius: '10px',
  bgcolor: 'var(--q-tooltip-bg)',
  color: 'var(--q-tooltip-text)',
  boxShadow: 'var(--q-shadow-xl)',
};

/**
 * Selected image (NodeSelection) of an editor state, or null.
 * @param {import('@tiptap/pm/state').EditorState} state - Editor state.
 * @returns {{pos: number, node: import('@tiptap/pm/model').Node}|null} Image.
 */
export const selectedImage = (state) => {
  const node = state?.selection?.node;
  return node && node.type.name === 'image' ? { pos: state.selection.from, node } : null;
};

/** Bubble menu plugin key (also the meta key that hides it). */
const IMAGE_MENU_KEY = 'quillImageMenu';

/**
 * Image toolbar bubble menu. Kept mounted for the editor's lifetime with a stable shouldShow.
 * @param {{editor: import('@tiptap/core').Editor}} props - Props.
 * @returns {JSX.Element}
 */
const ImageToolbar = memo(({ editor }) => {
  const paperRef = useRef(null);
  const fileRef = useRef(null);
  const replaceTarget = useRef(null);
  const [altDraft, setAltDraft] = useState(null);

  const image = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      const hit = ed ? selectedImage(ed.state) : null;
      if (!hit) return null;
      const { attrs } = hit.node;
      return {
        pos: hit.pos,
        align: normalizeImageAlign(attrs.align),
        wrap: normalizeImageWrap(attrs.wrap),
        width: Number(attrs.width) > 0 ? Number(attrs.width) : null,
        height: Number(attrs.height) > 0 ? Number(attrs.height) : null,
        alt: attrs.alt || '',
      };
    },
  });

  // A new selection closes the alt text field.
  const imagePos = image?.pos ?? null;
  useEffect(() => setAltDraft(null), [imagePos]);

  /** Edits are allowed: the editor is editable and no review is open. */
  const canEdit = useCallback(
    () => Boolean(editor && !editor.isDestroyed && editor.isEditable && !editor.storage.quillRuntime?.reviewActive),
    [editor]
  );
  const [editable, setEditable] = useState(canEdit);
  // setEditable(false) (and opening a review) changes neither the selection nor the document,
  // so the bubble menu does not re-check shouldShow: hide it here, and keep its buttons inert.
  // The host's setEditorEditable emits no 'update' or 'transaction', only EDITABLE_EVENT.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    let was = canEdit();
    let timer = null;
    const check = () => {
      if (editor.isDestroyed) return;
      const next = canEdit();
      setEditable(next);
      if (was && !next && !timer) {
        timer = setTimeout(() => {
          timer = null;
          if (!editor.isDestroyed && !canEdit()) editor.view.dispatch(editor.state.tr.setMeta(IMAGE_MENU_KEY, 'hide'));
        }, 0);
      }
      was = next;
    };
    check();
    editor.on('update', check);
    editor.on('transaction', check);
    editor.on(EDITABLE_EVENT, check);
    return () => {
      clearTimeout(timer);
      editor.off('update', check);
      editor.off('transaction', check);
      editor.off(EDITABLE_EVENT, check);
    };
  }, [editor, canEdit]);
  useEffect(() => {
    if (!editable) setAltDraft(null);
  }, [editable]);

  const shouldShow = useCallback(({ editor: ed, state: pmState }) => {
    if (!ed || !ed.isEditable) return false;
    if (ed.storage.quillRuntime?.reviewActive) return false;
    return Boolean(selectedImage(pmState));
  }, []);
  const options = useMemo(() => ({ placement: 'top', offset: 8 }), []);

  /** Apply attributes to the selected image, keeping it selected. */
  const apply = useCallback((attrs, pos = imagePos) => {
    if (pos == null || !canEdit()) return;
    const node = editor.state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'image') return;
    editor.chain().setNodeSelection(pos).updateAttributes('image', attrs).run();
  }, [editor, imagePos, canEdit]);

  const nodeElement = useCallback(() => {
    if (imagePos == null || !editor || editor.isDestroyed) return null;
    try {
      return editor.view.nodeDOM(imagePos);
    } catch {
      return null;
    }
  }, [editor, imagePos]);

  const setSize = useCallback((preset) => {
    if (!image) return;
    const element = nodeElement();
    const img = element?.tagName === 'IMG' ? element : element?.querySelector?.('img');
    const size = presetImageSize({
      ratio: preset.ratio,
      contentWidth: contentWidthOf(element),
      naturalWidth: img?.naturalWidth,
      naturalHeight: img?.naturalHeight,
      width: image.width,
      height: image.height,
    });
    // Full width spans the column, and Large is wider than a wrapped image can be drawn
    // (WRAPPED_IMAGE_MAX_SHARE), so neither keeps text beside it.
    const placement = placementForSize(preset, image.align, image.wrap);
    apply(placement ? { ...size, ...placement } : size);
  }, [apply, image, nodeElement]);

  const startReplace = useCallback(() => {
    if (!canEdit()) return;
    replaceTarget.current = imagePos;
    fileRef.current?.click();
  }, [imagePos, canEdit]);

  const finishReplace = useCallback(async (file) => {
    const pos = replaceTarget.current;
    replaceTarget.current = null;
    if (!file || pos == null || !file.type?.startsWith('image/') || !canEdit()) return;
    const before = editor.state.doc.nodeAt(pos);
    if (!before || before.type.name !== 'image') return;
    try {
      const upload = await imageService.uploadImage(file);
      if (!upload || !canEdit()) return;
      // The document may have changed while uploading: find the same picture again.
      let target = null;
      const at = editor.state.doc.nodeAt(pos);
      if (at && at.type.name === 'image' && at.attrs.src === before.attrs.src) target = pos;
      if (target == null) {
        editor.state.doc.descendants((node, nodePos) => {
          if (target != null) return false;
          if (node.type.name === 'image' && node.attrs.src === before.attrs.src) target = nodePos;
          return true;
        });
      }
      if (target == null) return;
      const current = editor.state.doc.nodeAt(target);
      editor.chain().focus().setNodeSelection(target).updateAttributes('image', replacedImageAttrs(current.attrs, upload)).run();
    } catch (error) {
      console.error('Image replace failed:', error);
    }
  }, [editor, canEdit]);

  const commitAlt = useCallback(() => {
    if (altDraft === null || !canEdit()) return;
    apply({ alt: altDraft.trim() });
    setAltDraft(null);
    editor.commands.focus();
  }, [altDraft, apply, editor, canEdit]);

  // Alt+F10 in the editor moves focus into the toolbar while an image is selected.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const dom = editor.view.dom;
    const onKeyDown = (event) => {
      if (!(event.altKey && event.key === 'F10')) return;
      if (!selectedImage(editor.state) || !canEdit()) return;
      const first = paperRef.current?.querySelector('button, input');
      if (!first) return;
      event.preventDefault();
      first.focus();
    };
    dom.addEventListener('keydown', onKeyDown);
    return () => dom.removeEventListener('keydown', onKeyDown);
  }, [editor, canEdit]);

  /** Arrow keys move between buttons; Escape returns to the editor. */
  const onToolbarKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setAltDraft(null);
      editor.commands.focus();
      return;
    }
    if (event.target?.tagName === 'INPUT') return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return;
    const buttons = Array.from(paperRef.current?.querySelectorAll('button') || []);
    if (!buttons.length) return;
    const index = buttons.indexOf(document.activeElement);
    let next = index;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else next = (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    event.preventDefault();
    buttons[next]?.focus();
  }, [editor]);

  const size = image ? activeSizePreset(image.width, contentWidthOf(nodeElement()), image.wrap) : null;
  const wrapped = image?.wrap === 'left' || image?.wrap === 'right';

  return (
    <>
      <BubbleMenu editor={editor} pluginKey={IMAGE_MENU_KEY} shouldShow={shouldShow} options={options}>
        {!editable ? null : altDraft !== null ? (
          <Paper
            ref={paperRef}
            elevation={0}
            sx={{ ...paperSx, px: 1, flexWrap: 'nowrap' }}
            component="form"
            onKeyDown={onToolbarKeyDown}
            onSubmit={(e) => {
              e.preventDefault();
              commitAlt();
            }}
          >
            <InputBase
              autoFocus
              value={altDraft}
              onChange={(e) => setAltDraft(e.target.value.slice(0, 500))}
              placeholder="Describe the image for screen readers"
              inputProps={{ 'aria-label': 'Image alt text', maxLength: 500 }}
              sx={{ color: 'var(--q-tooltip-text)', fontSize: 13, width: 260, maxWidth: '60vw', px: 0.5 }}
            />
            <ToolButton label="Save alt text" toggle={false} onClick={commitAlt}><Check /></ToolButton>
          </Paper>
        ) : (
          <Paper ref={paperRef} elevation={0} sx={paperSx} role="toolbar" aria-label="Image options" onKeyDown={onToolbarKeyDown}>
            <ToolButton label="Align left" active={image?.align === 'left'} onClick={() => apply(placementForAlign('left', image?.wrap))}><FormatAlignLeft /></ToolButton>
            <ToolButton label="Align centre" active={image?.align === 'center'} onClick={() => apply(placementForAlign('center', image?.wrap))}><FormatAlignCenter /></ToolButton>
            <ToolButton label="Align right" active={image?.align === 'right'} onClick={() => apply(placementForAlign('right', image?.wrap))}><FormatAlignRight /></ToolButton>
            <Divider />
            <ToolButton label="In line with text" active={image?.wrap === 'none'} onClick={() => apply(placementForWrap('none', image?.align))}><WrapNoneIcon /></ToolButton>
            <ToolButton label="Wrap text, image on the left" active={image?.wrap === 'left'} onClick={() => apply(placementForWrap('left', image?.align))}><WrapLeftIcon /></ToolButton>
            <ToolButton label="Wrap text, image on the right" active={image?.wrap === 'right'} onClick={() => apply(placementForWrap('right', image?.align))}><WrapRightIcon /></ToolButton>
            <Divider />
            {IMAGE_SIZE_PRESETS.map((preset) => (
              <ToolButton
                key={preset.id}
                wide
                label={`${preset.label} (${Math.round(preset.ratio * 100)}% of the text width${wrapped && presetStopsWrap(preset) ? ', stops text wrapping' : ''})`}
                active={size === preset.id}
                onClick={() => setSize(preset)}
              >
                {preset.short}
              </ToolButton>
            ))}
            <Divider />
            <ToolButton label="Replace image" toggle={false} onClick={startReplace}><SwapHoriz /></ToolButton>
            <ToolButton label={image?.alt ? `Alt text: ${image.alt}` : 'Add alt text'} active={Boolean(image?.alt)} toggle={false} wide onClick={() => setAltDraft(image?.alt || '')}>
              Alt
            </ToolButton>
          </Paper>
        )}
      </BubbleMenu>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        data-testid="image-replace-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          finishReplace(file);
        }}
      />
    </>
  );
});
ImageToolbar.displayName = 'ImageToolbar';

export default ImageToolbar;
